/**
 * What to send to the engine for one data tile right now: its conditions
 * (dashboard filters, other tiles' cross-filters, its own base) composed
 * into a universe, and the tile script built against it. Filter categories
 * the codebook does not list are ignored (see `liveFilterSelections`). The
 * universe declaration is emitted only while the session does not know the
 * field yet. A repeated tile also gets one plan per panel. While the
 * dashboard has a weight, every script starts with its own weight line:
 * the weight, or none for an unweighted tile (see `buildTileScript`).
 */

import { STATISTIC_TYPE } from "types/ReportingDashboard/elements.types"
import { isDataTile } from "features/Dashboard/types/dashboardDocument"
import { DashboardState, findTile } from "./dashboardStore"
import {
  collectConditionGroups,
  composeUniverse,
  liveFilterSelections,
  UniverseSpec,
} from "./universeComposer"
import { buildTileScript, leavesOfCodebook, TileWeight } from "./tileScript"
import { panelCategories, panelCondition, repeatOf } from "./panels"

/** One panel of a repeated tile: its category and what it runs. */
export interface PanelPlan {
  /** The panel's title. */
  label: string
  category: string
  script: string
  usesUniverse: boolean
  universe: UniverseSpec | null
  emitPrefix: boolean
  /** See the tile plan's `quietScript`. */
  quietScript?: string
}

export const planTileRun = (
  state: DashboardState,
  tileId: string
): {
  script: string
  usesUniverse: boolean
  outputIndex: number
  universe: UniverseSpec | null
  emitPrefix: boolean
  /**
   * Set when `script` declares its universe: the same script without the
   * declaration, which is what the next plan sends once the session knows
   * the field. A successful run caches its result for it too, so a re-plan
   * (a Retry of a repeated tile's failed panels) does not run it again.
   */
  quietScript?: string
  /**
   * Set when the tile repeats by a variable: one plan per category (the
   * first `MAX_PANELS`), each with the category as one more condition
   * group. Empty when the codebook lists no category of the variable.
   */
  panels?: PanelPlan[]
} | null => {
  const found = findTile(state, tileId)
  if (!found || !isDataTile(found.tile)) return null
  const tile = found.tile
  const groupsWith = (tileConditions: string[][]) =>
    collectConditionGroups({
      filters: state.document.filters,
      filterSelections: liveFilterSelections(
        state.document.filters,
        state.filterSelections,
        state.codebook
      ),
      crossFilters: state.crossFilters,
      tileId,
      receives: tile.crossFilter.receives,
      tileConditions,
    })
  const controls = state.tileControls[tileId]
  const leavesOf = leavesOfCodebook(state.codebook)
  // A rolling average weights by the base, so a Statistic pick keeps "n".
  // A transposed table shows no measures, so it keeps nothing.
  const transposed = tile.type === "table" && tile.visual.transposed
  const keepStatistics =
    !transposed && tile.measures.some((m) => m.kind === "rolling")
      ? [STATISTIC_TYPE.N]
      : undefined
  // Both source kinds carry the base (a script tile keeps its builder's).
  const weight: TileWeight = {
    active: state.document.data.weight,
    unweighted: tile.unweighted,
  }
  /** The script for `universe`, and without its declaration when it has one. */
  const scriptFor = (
    u: UniverseSpec | null,
    emit: boolean,
    panel?: number
  ): ReturnType<typeof buildTileScript> & { quietScript?: string } => {
    const args = {
      tile,
      universe: u,
      controls,
      leavesOf,
      keepStatistics,
      weight,
      panel,
    }
    const built = buildTileScript({ ...args, emitPrefix: emit })
    return emit
      ? {
          ...built,
          quietScript: buildTileScript({ ...args, emitPrefix: false }).script,
        }
      : built
  }
  const universe = composeUniverse(groupsWith(tile.source.conditions))
  const emitPrefix =
    universe !== null &&
    !state.session.knownFilterFields.has(universe.fieldName)
  const plan = scriptFor(universe, emitPrefix)
  const repeat = repeatOf(tile)
  if (!repeat) return { ...plan, universe, emitPrefix }

  // A universe the session does not know is declared by the first panel
  // that needs it; the panels run in order on one queue. Panel k (from 1)
  // writes its own result table, `t_<tile>_<k>`, whatever the source kind.
  const declared = new Set(state.session.knownFilterFields)
  const panels = panelCategories(state.codebook, repeat.variable).map(
    (category, k): PanelPlan => {
      const panelUniverse = composeUniverse(
        groupsWith([
          ...tile.source.conditions,
          panelCondition(repeat.variable, category),
        ])
      )
      const emit =
        panelUniverse !== null && !declared.has(panelUniverse.fieldName)
      if (panelUniverse && emit) declared.add(panelUniverse.fieldName)
      const panel = scriptFor(panelUniverse, emit, k + 1)
      return {
        label: category,
        category,
        script: panel.script,
        usesUniverse: panel.usesUniverse,
        universe: panelUniverse,
        emitPrefix: emit,
        ...(panel.quietScript !== undefined
          ? { quietScript: panel.quietScript }
          : {}),
      }
    }
  )
  return { ...plan, universe, emitPrefix, panels }
}
